import { useCallback, useEffect } from 'react';
import { UseInfiniteScrollOptions } from '../types';

// 🎯 Hook reutilizable para infinite scroll - Dependency Inversion Principle
export const useInfiniteScroll = ({
    parentRef,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    threshold = 200
}: UseInfiniteScrollOptions): void => {

    // 🎯 Memoized scroll handler para performance
    const handleScroll = useCallback(() => {
        const scrollElement = parentRef.current;
        if (!scrollElement || !fetchNextPage || !hasNextPage || isFetchingNextPage) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const scrollPosition = scrollTop + clientHeight;
        const shouldFetch = scrollHeight - scrollPosition <= threshold;

        if (shouldFetch) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, threshold, parentRef]);

    // 🎯 Effect para attach/detach event listener
    useEffect(() => {
        const scrollElement = parentRef.current;
        if (!scrollElement || !fetchNextPage) return;

        // 🎯 Throttle scroll events para performance
        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        scrollElement.addEventListener('scroll', throttledScroll, { passive: true });

        return () => {
            scrollElement.removeEventListener('scroll', throttledScroll);
        };
    }, [handleScroll, parentRef, fetchNextPage]);
}; 